/**
 * Agent Contact Form Component
 * 에이전트 컨택 폼 - Realestate.com.au 스타일
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Building2, Phone, Mail } from "lucide-react";
import { useState } from "react";
import { Property } from "@shared/schemas/properties";

interface AgentContactFormProps {
  property: Property;
  agentName?: string;
  agentAvatar?: string;
  agentPhone?: string;
  agentEmail?: string;
  businessName?: string;
}

export function AgentContactForm({
  property,
  agentName,
  agentAvatar,
  agentPhone,
  agentEmail,
  businessName,
}: AgentContactFormProps) {
  const [enquiryTypes, setEnquiryTypes] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatPrice = (cents: number | null | undefined, period?: string | null) => {
    if (cents === null || cents === undefined) return "가격 문의";
    if (cents === 0) return "무료";
    const price = Math.round(cents / 100);
    if (period === "weekly") {
      return `$${price.toLocaleString()}/week`;
    } else if (period === "monthly") {
      return `$${price.toLocaleString()}/month`;
    }
    return `$${price.toLocaleString()}`;
  };

  const toggleEnquiryType = (type: string) => {
    setEnquiryTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // API 엔드포인트 호출
      const response = await fetch(`/api/properties/${property.id}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          enquiry_types: enquiryTypes,
          message,
          name,
          email,
          phone,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send enquiry");
      }

      // 성공 메시지 표시
      alert("문의가 성공적으로 전송되었습니다.");

      // 폼 초기화
      setEnquiryTypes([]);
      setMessage("");
      setName("");
      setEmail("");
      setPhone("");
    } catch (error) {
      console.error("Error sending enquiry:", error);
      alert("문의 전송에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 이미지 배열 생성
  const allImages: string[] = [];
  if (property.primaryImage) {
    allImages.push(property.primaryImage);
  }
  if (property.images && Array.isArray(property.images)) {
    property.images.forEach((img) => {
      if (img && !allImages.includes(img)) {
        allImages.push(img);
      }
    });
  }
  const thumbnailImage = allImages[0] || null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          Email enquiry to {businessName || "Agent"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 에이전트 정보 */}
          <div className="flex items-center gap-3 pb-4 border-b">
            <Avatar>
              <AvatarImage src={agentAvatar} />
              <AvatarFallback>
                {agentName?.[0] || <Building2 className="w-4 h-4" />}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-semibold text-primary">{agentName || "에이전트"}</p>
              {agentPhone && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-1 h-7 text-xs"
                  onClick={() => {
                    window.location.href = `tel:${agentPhone}`;
                  }}
                >
                  <Phone className="w-3 h-3 mr-1" />
                  {agentPhone.substring(0, 8)}..
                </Button>
              )}
            </div>
          </div>

          {/* 매물 정보 */}
          <div className="flex items-start gap-3 pb-4 border-b">
            {thumbnailImage && (
              <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                <img
                  src={thumbnailImage}
                  alt={property.listingType}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23e5e7eb" width="200" height="200"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="16" dy="105" dx="100" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">
                {property.address || `${property.suburb || property.city}, ${property.state}`}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {formatPrice(property.price, property.pricePeriod)}
              </p>
            </div>
          </div>

          {/* Enquiry 타입 */}
          <div>
            <Label className="text-sm font-medium mb-3 block">
              What's your enquiry about?
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="available_date"
                  checked={enquiryTypes.includes("available_date")}
                  onCheckedChange={() => toggleEnquiryType("available_date")}
                />
                <Label htmlFor="available_date" className="text-sm cursor-pointer">
                  Available date
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="inspection"
                  checked={enquiryTypes.includes("inspection")}
                  onCheckedChange={() => toggleEnquiryType("inspection")}
                />
                <Label htmlFor="inspection" className="text-sm cursor-pointer">
                  Inspection
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="length_of_lease"
                  checked={enquiryTypes.includes("length_of_lease")}
                  onCheckedChange={() => toggleEnquiryType("length_of_lease")}
                />
                <Label htmlFor="length_of_lease" className="text-sm cursor-pointer">
                  Length of lease
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rental_application"
                  checked={enquiryTypes.includes("rental_application")}
                  onCheckedChange={() => toggleEnquiryType("rental_application")}
                />
                <Label htmlFor="rental_application" className="text-sm cursor-pointer">
                  Rental application
                </Label>
              </div>
            </div>
          </div>

          {/* 메시지 */}
          <div>
            <Label htmlFor="message" className="text-sm font-medium mb-2 block">
              Message
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="메시지를 입력하세요..."
              className="min-h-[120px] resize-y"
            />
          </div>

          {/* 연락처 정보 */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium mb-2 block">
                Name <span className="text-red-500">(required)</span>
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-2 block">
                Email address <span className="text-red-500">(required)</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-sm font-medium mb-2 block">
                Phone number
              </Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>

          {/* 제출 버튼 */}
          <Button
            type="submit"
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? "전송 중..." : "Send enquiry"}
          </Button>

          {/* 법적 고지 */}
          <div className="pt-4 border-t text-xs text-muted-foreground space-y-2">
            <a href="#" className="text-primary hover:underline">
              Personal Information Collection Statement
            </a>
            <p className="text-xs">
              This form is only to be used for sending genuine email enquiries to the Agent.
              OZK reserves its right to take any legal or other appropriate action in relation
              to misuse of this service.
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

